import { useEffect, useState } from "react";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { Copy, Check, Gift, Users, Sparkles, Send, Mail } from "lucide-react";

const NAV = [
  { to: "/founder/dashboard", label: "Dashboard" },
  { to: "/founder/tutorial", label: "Tutorial" },
  { to: "/founder/team", label: "Team" },
  { to: "/onboarding", label: "Onboarding" },
];

const STATUS_META = {
  signed_up: { label: "Signed up", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  completed: { label: "Completed analysis", cls: "bg-green-50 text-green-700 border-green-200" },
};

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div data-testid={testId}
      className="rounded-2xl border border-neutral-200 bg-white p-4 sm:p-5">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className="mt-2 text-3xl font-extrabold text-neutral-900">{value}</div>
    </div>
  );
}

export default function FounderReferrals() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/founder/referrals/me");
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e?.message || "Couldn't load referrals.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleCopy = async () => {
    if (!data?.share_url) return;
    try {
      await navigator.clipboard.writeText(data.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (_e) {
      // Fallback: select-on-focus is good enough
    }
  };

  const shareViaEmail = () => {
    if (!data?.share_url) return;
    const subject = encodeURIComponent("Get your Capital Readiness Score with Clinq");
    const body = encodeURIComponent(
      `Hey,\n\nI've been using Clinq to get a funder-grade Capital Readiness Score for my business. ` +
      `It plugs you into the African investor network and gives you a clear roadmap to be funder-ready.\n\n` +
      `Sign up with my link and you'll get a bonus re-analysis credit on me:\n${data.share_url}\n\n— `
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareViaWhatsApp = () => {
    if (!data?.share_url) return;
    const text = encodeURIComponent(
      `Get your Capital Readiness Score with Clinq — and a bonus re-analysis credit when you sign up with my link: ${data.share_url}`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
  };

  const shareViaLinkedIn = () => {
    if (!data?.share_url) return;
    const url = encodeURIComponent(data.share_url);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      "_blank", "noopener");
  };

  return (
    <AppShell nav={NAV}>
      <div data-testid="founder-referrals-page" className="space-y-6">
        <div>
          <h1 className="text-2xl font-extrabold text-neutral-900 sm:text-3xl"
              data-testid="referrals-heading">
            Refer & Earn
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-neutral-600" data-testid="referrals-subtitle">
            Invite other African founders to Clinq. They get a bonus re-analysis credit
            on sign-up. You get <span className="font-semibold text-neutral-900">+1
            re-analysis credit</span> the moment they complete their first AI analysis.
          </p>
        </div>

        {err && (
          <div data-testid="referrals-error"
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {err}
          </div>
        )}

        {loading && !data && (
          <div className="grid gap-4 sm:grid-cols-3">
            {[0,1,2].map(i => (
              <div key={i} className="h-24 animate-pulse rounded-2xl border border-neutral-200 bg-neutral-50" />
            ))}
          </div>
        )}

        {data && (
          <>
            {/* Stats */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard icon={Users}    label="Invited"          value={data.stats.invited}        testId="stat-invited" />
              <StatCard icon={Sparkles} label="Signed up"        value={data.stats.signed_up}      testId="stat-signed-up" />
              <StatCard icon={Gift}     label="Completed"        value={data.stats.completed}      testId="stat-completed" />
              <StatCard icon={Gift}     label="Credits earned"   value={data.stats.credits_earned} testId="stat-credits-earned" />
            </div>

            {/* Share card */}
            <div className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6"
                 data-testid="referrals-share-card">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
                    Your referral link
                  </div>
                  <div className="mt-1 text-sm text-neutral-600">
                    Code: <span data-testid="referral-code"
                      className="rounded bg-neutral-100 px-2 py-0.5 font-mono text-[13px] font-bold text-neutral-900">
                      {data.code}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-900">
                  <Gift className="h-3.5 w-3.5" /> +1 credit per successful referral
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <input
                  data-testid="referral-share-input"
                  readOnly
                  value={data.share_url}
                  onFocus={(e) => e.target.select()}
                  className="w-full flex-1 truncate rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5 font-mono text-sm text-neutral-700 outline-none focus:border-[#0A8B85]"
                />
                <button
                  data-testid="referral-copy-btn"
                  onClick={handleCopy}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#0A8B85] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#066667]">
                  {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy link</>}
                </button>
              </div>

              {/* Share quick actions */}
              <div className="mt-4 flex flex-wrap gap-2">
                <button data-testid="referral-share-email" onClick={shareViaEmail}
                  className="inline-flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-50">
                  <Mail className="h-3.5 w-3.5" /> Share via email
                </button>
                <button data-testid="referral-share-whatsapp" onClick={shareViaWhatsApp}
                  className="inline-flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-50">
                  <Send className="h-3.5 w-3.5" /> WhatsApp
                </button>
                <button data-testid="referral-share-linkedin" onClick={shareViaLinkedIn}
                  className="inline-flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-50">
                  <Send className="h-3.5 w-3.5" /> LinkedIn
                </button>
              </div>

              {/* How it works */}
              <div className="mt-5 grid gap-3 rounded-xl border border-neutral-200 bg-neutral-50 p-4 sm:grid-cols-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#0A8B85]">Step 1</div>
                  <div className="mt-1 text-sm font-semibold text-neutral-900">Share your link</div>
                  <div className="text-xs text-neutral-600">Send it to a fellow founder.</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#0A8B85]">Step 2</div>
                  <div className="mt-1 text-sm font-semibold text-neutral-900">They sign up</div>
                  <div className="text-xs text-neutral-600">They get +1 bonus re-analysis credit on the house.</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#0A8B85]">Step 3</div>
                  <div className="mt-1 text-sm font-semibold text-neutral-900">You both grow</div>
                  <div className="text-xs text-neutral-600">When they finish their first AI analysis, you get +1 credit too.</div>
                </div>
              </div>
            </div>

            {/* Referrals table */}
            <div className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6"
                 data-testid="referrals-list-card">
              <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                  <h2 className="text-base font-bold text-neutral-900">Your referrals</h2>
                  <p className="text-xs text-neutral-500">Updated in real time as your invitees progress.</p>
                </div>
              </div>

              {(data.referrals || []).length === 0 ? (
                <div data-testid="referrals-empty"
                  className="mt-4 rounded-xl border border-dashed border-neutral-200 bg-neutral-50 px-4 py-8 text-center text-sm text-neutral-600">
                  No referrals yet. Copy your link above and send it to a founder you know.
                </div>
              ) : (
                <div className="mt-4 overflow-x-auto">
                  <table data-testid="referrals-table" className="w-full text-left text-sm">
                    <thead className="text-xs uppercase tracking-wider text-neutral-500">
                      <tr>
                        <th className="py-2 pr-3 font-semibold">Founder</th>
                        <th className="py-2 pr-3 font-semibold">Status</th>
                        <th className="py-2 pr-3 font-semibold">Signed up</th>
                        <th className="py-2 pr-3 font-semibold">Completed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.referrals.map(r => {
                        const meta = STATUS_META[r.status] || STATUS_META.signed_up;
                        return (
                          <tr key={r.referral_id}
                              data-testid={`referral-row-${r.referral_id}`}
                              className="border-t border-neutral-100">
                            <td className="py-3 pr-3 text-neutral-800">{r.referred_email_masked}</td>
                            <td className="py-3 pr-3">
                              <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}>
                                {meta.label}
                              </span>
                            </td>
                            <td className="py-3 pr-3 text-neutral-500">
                              {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                            </td>
                            <td className="py-3 pr-3 text-neutral-500">
                              {r.completed_at ? new Date(r.completed_at).toLocaleDateString() : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
